import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './StudentProfile.css';

// Import a default avatar as a module
import defaultAvatar from '/default-avatar.jpg';
import logo from './images/logo.jpg';
const StudentProfile = () => {
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [theme, setTheme] = useState('light');
  const [seasonTheme, setSeasonTheme] = useState('winter');
  const [adminMessages, setAdminMessages] = useState([]);
  const [studentMessages, setStudentMessages] = useState([]);
  const [currentCourses, setCurrentCourses] = useState([]);
  const [completedCourses, setCompletedCourses] = useState([]);
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [newMessage, setNewMessage] = useState({ subject: '', message: '' });
  const [avatarError, setAvatarError] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const navigate = useNavigate();

  // API Base URL
  const API_BASE = 'http://localhost:8081';

  // Seasonal themes configuration
  const seasonalThemes = {
    winter: {
      primary: '#6366f1',
      secondary: '#8b5cf6',
      accent: '#a855f7',
      background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%)'
    },
    spring: {
      primary: '#10b981',
      secondary: '#34d399',
      accent: '#6ee7b7',
      background: 'linear-gradient(135deg, #0f1a15 0%, #1a2e24 100%)'
    },
    summer: {
      primary: '#f59e0b',
      secondary: '#fbbf24',
      accent: '#fcd34d',
      background: 'linear-gradient(135deg, #1a150f 0%, #2e281a 100%)'
    },
    autumn: {
      primary: '#ef4444',
      secondary: '#f87171',
      accent: '#fca5a5',
      background: 'linear-gradient(135deg, #1a0f0f 0%, #2e1a1a 100%)'
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userRole = localStorage.getItem('userRole');
    
    if (!token) {
      navigate('/');
      return;
    }

    fetchAllStudentData();
  }, [navigate]);

  const fetchAllStudentData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const studentId = localStorage.getItem('userId') || '1';

      console.log('🔍 Starting comprehensive data fetch for student');

      // Fetch all data in parallel
      const [profileResponse, studiesResponse, messagesResponse, sentMessagesResponse] = await Promise.all([
        fetch(`${API_BASE}/api/student/profile`, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'X-Student-ID': studentId
          }
        }),
        fetch(`${API_BASE}/api/student/studies`, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'X-Student-ID': studentId
          }
        }),
        fetch(`${API_BASE}/api/student/messages`, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'X-Student-ID': studentId
          }
        }),
        fetch(`${API_BASE}/api/student/sent-messages`, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'X-Student-ID': studentId
          }
        })
      ]);

      // Check if all responses are OK
      if (!profileResponse.ok || !studiesResponse.ok || !messagesResponse.ok || !sentMessagesResponse.ok) {
        throw new Error('One or more API requests failed');
      }

      // Process responses
      const profileData = await profileResponse.json();
      const studiesData = await studiesResponse.json();
      const messagesData = await messagesResponse.json();
      const sentMessagesData = await sentMessagesResponse.json();

      console.log('📊 Data fetched from database:', {
        profile: profileData,
        studies: studiesData,
        messages: messagesData,
        sentMessages: sentMessagesData
      });

      // Process and set all data
      processStudentData(profileData, studiesData, messagesData, sentMessagesData);

    } catch (error) {
      console.error('🚨 Error fetching student data:', error);
      setError('Failed to load data from server. Please check if the backend is running.');
      setLoading(false);
    }
  };

  const processStudentData = (profileData, studiesData = [], adminMessagesData = [], studentMessagesData = []) => {
    console.log('🔄 Processing real data from database:', {
      profileData,
      studiesCount: studiesData.length,
      adminMessagesCount: adminMessagesData.length,
      studentMessagesCount: studentMessagesData.length
    });

    // Process studies data into current and completed courses
    const currentCoursesData = studiesData
      .filter(study => study.status === 'in_progress')
      .map(study => ({
        id: study.id,
        name: `${getSubjectDisplayName(study.subject)} - ${study.level_name || getLevelName(study.level_id)}`,
        progress: calculateProgress(study.start_date, study.status),
        teacher: getTeacherBySubject(study.subject),
        subject: study.subject,
        level: study.level_name || getLevelName(study.level_id),
        level_id: study.level_id,
        start_date: study.start_date,
        status: study.status,
        end_date: study.end_date
      }));

    const completedCoursesData = studiesData
      .filter(study => study.status === 'completed')
      .map(study => ({
        id: study.id,
        name: `${getSubjectDisplayName(study.subject)} - ${study.level_name || getLevelName(study.level_id)}`,
        completedDate: study.end_date || study.updated_at,
        grade: calculateGrade(study.subject, study.level_id)
      }));

    // Generate upcoming classes based on current courses
    const upcomingClassesData = generateUpcomingClasses(currentCoursesData);

    // Process admin messages - ensure they have proper structure
    const processedAdminMessages = Array.isArray(adminMessagesData) ? adminMessagesData.map(msg => ({
      id: msg.id,
      message_id: msg.message_id,
      subject: msg.subject || 'No Subject',
      message: msg.message || 'No message content',
      created_at: msg.created_at,
      is_read: Boolean(msg.is_read),
      read_at: msg.read_at,
      sender: 'Admin'
    })) : [];

    // Process student messages
    const processedStudentMessages = Array.isArray(studentMessagesData) ? studentMessagesData.map(msg => ({
      id: msg.id,
      subject: msg.subject || 'No Subject',
      message: msg.message || 'No message content',
      created_at: msg.created_at,
      sender: 'You'
    })) : [];

    // Generate achievements based on completed courses
    const achievementsData = generateAchievements(completedCoursesData);

    // Set all state with real data
    setStudentData(profileData);
    setCurrentCourses(currentCoursesData);
    setCompletedCourses(completedCoursesData);
    setAdminMessages(processedAdminMessages);
    setStudentMessages(processedStudentMessages);
    setUpcomingClasses(upcomingClassesData);
    setAchievements(achievementsData);
    setLoading(false);

    console.log('✅ Final state set with REAL database data:', {
      currentCourses: currentCoursesData.length,
      completedCourses: completedCoursesData.length,
      adminMessages: processedAdminMessages.length,
      studentMessages: processedStudentMessages.length,
      achievements: achievementsData.length
    });
  };

  const calculateProgress = (startDate, status) => {
    if (status === 'completed') return 100;
    if (status === 'failed' || status === 'dropped') return 0;
    
    if (!startDate) return 30;
    
    try {
      const start = new Date(startDate);
      const now = new Date();
      const monthsPassed = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
      const progress = Math.min(monthsPassed * 25, 95);
      return progress > 0 ? progress : 30;
    } catch (error) {
      return 30;
    }
  };

  const calculateGrade = (subject, levelId) => {
    // Simple grade calculation based on subject and level
    const baseGrades = {
      english: ['A', 'B+', 'A-'],
      french: ['B+', 'A', 'A-'],
      italian: ['A-', 'B+', 'A'],
      robotics_beginners: ['A', 'A', 'B+'],
      robotics_medium: ['A-', 'B+', 'A'],
      robotics_advanced: ['A', 'A-', 'A']
    };
    
    const grades = baseGrades[subject] || ['A', 'B+', 'A-'];
    return grades[levelId % grades.length];
  };

  const generateAchievements = (completedCourses) => {
    if (completedCourses.length === 0) return [];

    const achievements = [];
    const now = new Date();

    // Course completion achievements
    completedCourses.forEach((course, index) => {
      achievements.push({
        id: `course-${course.id}`,
        name: `${course.name} Completion`,
        description: `Successfully completed ${course.name}`,
        icon: 'fas fa-graduation-cap',
        date: course.completedDate || now.toISOString(),
        type: 'course_completion'
      });
    });

    // Level-based achievements
    if (completedCourses.some(course => course.level_id >= 6)) {
      achievements.push({
        id: 'b1-achievement',
        name: 'B1 Level Master',
        description: 'Reached B1 level in any language',
        icon: 'fas fa-trophy',
        date: now.toISOString(),
        type: 'level_master'
      });
    }

    // Subject count achievements
    const uniqueSubjects = [...new Set(completedCourses.map(course => course.subject))];
    if (uniqueSubjects.length >= 2) {
      achievements.push({
        id: 'multi-subject',
        name: 'Multi-Linguist',
        description: 'Completed courses in multiple subjects',
        icon: 'fas fa-globe',
        date: now.toISOString(),
        type: 'multi_subject'
      });
    }

    return achievements.slice(0, 5);
  };

  const getSubjectDisplayName = (subject) => {
    const subjectNames = {
      english: "English",
      french: "French", 
      italian: "Italian",
      robotics_beginners: "Robotics Beginners",
      robotics_medium: "Robotics Medium",
      robotics_advanced: "Robotics Advanced"
    };
    return subjectNames[subject] || subject;
  };

  const getLevelName = (levelId) => {
    const levels = {
      1: "A1 Part One",
      2: "A1 Part Two", 
      3: "A2 Part One",
      4: "A2 Part Two",
      5: "A2 Plus",
      6: "B1 Part One",
      7: "B1 Part Two",
      8: "B1 Plus",
      9: "B2 Part One",
      10: "B2 Part Two",
      11: "B2 Plus",
      12: "C1",
      13: "C2"
    };
    return levels[levelId] || "Beginner";
  };

  const getTeacherBySubject = (subject) => {
    const teachers = {
      english: "Ms. Elena Rodriguez",
      robotics_beginners: "Prof. Ahmed Al-Mansoori", 
      french: "Mr. Marco Bellini",
      italian: "Italian Teacher",
      robotics_medium: "Robotics Medium Teacher",
      robotics_advanced: "Robotics Advanced Teacher"
    };
    return teachers[subject] || "Instructor";
  };

  const generateUpcomingClasses = (courses) => {
    if (!courses || courses.length === 0) return [];
    
    const classes = [];
    const today = new Date();
    
    courses.forEach((course, index) => {
      for (let i = 0; i < 2; i++) {
        const classDate = new Date(today);
        classDate.setDate(today.getDate() + (index * 2) + i + 1);
        
        if (classDate.getDay() !== 0 && classDate.getDay() !== 6) {
          classes.push({
            id: `${course.id}-${i}`,
            course: course.name,
            date: classDate.toISOString().split('T')[0],
            time: getClassTime(course.subject),
            teacher: course.teacher
          });
        }
      }
    });
    
    return classes.sort((a, b) => new Date(a.date) - new Date(b.date)).slice(0, 6);
  };

  const getClassTime = (subject) => {
    const times = {
      english: "14:00",
      french: "15:00",
      italian: "16:00", 
      robotics_beginners: "10:00",
      robotics_medium: "11:00",
      robotics_advanced: "12:00"
    };
    return times[subject] || "14:00";
  };

  // Improved image handling function
  const getStudentImage = () => {
    if (!studentData || avatarError) {
      return defaultAvatar;
    }
    
    // Use profile_picture from userData if available and exists
    if (studentData.profile_picture && studentData.profile_picture !== '/images/profile.jpg') {
      return studentData.profile_picture;
    }
    
    // Try to use default avatar for now to avoid 404 errors
    return defaultAvatar;
  };

  const handleAvatarError = () => {
    console.log('Avatar image failed to load, using default');
    setAvatarError(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userId');
    localStorage.removeItem('userData');
    navigate('/');
  };

  // Mark message as read
  const handleMarkAsRead = async (recipientId) => {
    try {
      const token = localStorage.getItem('authToken');
      const studentId = localStorage.getItem('userId') || '1';
      
      console.log(`📨 Marking message recipient ${recipientId} as read for student ${studentId}`);
      
      // Update local state first for immediate UI feedback
      setAdminMessages(prev => prev.map(msg => 
        msg.id === recipientId ? { ...msg, is_read: true, read_at: new Date().toISOString() } : msg
      ));
      
      // Call the Go backend to mark as read
      const response = await fetch(`${API_BASE}/api/student/messages/${recipientId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Student-ID': studentId
        }
      });
      
      console.log(`📨 Mark as read response:`, response.status);
      
      if (!response.ok) {
        console.warn('❌ Failed to mark message as read in backend');
        // Revert local state if backend call fails
        setAdminMessages(prev => prev.map(msg => 
          msg.id === recipientId ? { ...msg, is_read: false, read_at: null } : msg
        ));
        alert('Failed to mark message as read. Please try again.');
      } else {
        console.log('✅ Message marked as read successfully');
      }
      
    } catch (error) {
      console.error('💥 Error marking message as read:', error);
    }
  };

  // Send message to admin
  const handleSendMessage = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('authToken');

      console.log('📤 Sending message to admin:', newMessage);

      // Send message to Go backend
      const response = await fetch(`${API_BASE}/api/student/send-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          subject: newMessage.subject,
          message: newMessage.message
        })
      });

      console.log('📤 Send message response:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Message sent successfully:', result);
        
        setShowMessageModal(false);
        setNewMessage({ subject: '', message: '' });
        
        // Refresh all data to get the new sent message
        await fetchAllStudentData();
        
        alert('Message sent to admin successfully!');
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to send message:', errorText);
        throw new Error(`Failed to send message: ${response.status}`);
      }
    } catch (error) {
      console.error('💥 Error sending message:', error);
      alert('Failed to send message. Please check the console for details and try again.');
    }
  };

  // Helper function to render empty states
  const renderEmptyState = (type) => {
    const emptyStates = {
      courses: {
        icon: "fas fa-book",
        title: "No Courses Enrolled",
        message: "You are not enrolled in any courses yet. Please contact the administration to get started with your learning journey.",
        action: null
      },
      completed: {
        icon: "fas fa-graduation-cap",
        title: "No Completed Courses",
        message: "You haven't completed any courses yet. Keep learning and you'll see your completed courses here soon!",
        action: null
      },
      messages: {
        icon: "fas fa-envelope-open",
        title: "No Messages",
        message: "You don't have any messages from the administration at the moment. Check back later for updates.",
        action: () => setShowMessageModal(true)
      },
      sentMessages: {
        icon: "fas fa-paper-plane",
        title: "No Sent Messages",
        message: "You haven't sent any messages to the administration yet. Click the button below to send your first message.",
        action: () => setShowMessageModal(true)
      },
      classes: {
        icon: "fas fa-calendar",
        title: "No Upcoming Classes",
        message: "You don't have any classes scheduled for the upcoming days. Your schedule will appear here once classes are scheduled.",
        action: null
      },
      achievements: {
        icon: "fas fa-trophy",
        title: "No Achievements Yet",
        message: "Start completing courses and participating in activities to earn achievements! Your accomplishments will appear here.",
        action: null
      }
    };

    const state = emptyStates[type] || emptyStates.courses;

    return (
      <div className="empty-state">
        <div className="empty-state-icon">
          <i className={state.icon}></i>
        </div>
        <h3>{state.title}</h3>
        <p>{state.message}</p>
        {state.action && (
          <button className="btn-primary" onClick={state.action}>
            <i className="fas fa-paper-plane"></i>
            Send Your First Message
          </button>
        )}
      </div>
    );
  };

  const currentSeasonTheme = seasonalThemes[seasonTheme];

  if (loading) {
    return (
      <div className="student-profile-loading">
        <div className="loading-spinner"></div>
        <p>Loading your profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="student-profile-error">
        <h2>Error loading profile</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
        <button onClick={() => navigate('/')}>Return to Home</button>
      </div>
    );
  }

  if (!studentData) {
    return (
      <div className="student-profile-error">
        <h2>No Student Data</h2>
        <p>We couldn't load your student profile. Please try refreshing the page or contact support if the problem continues.</p>
        <button onClick={() => window.location.reload()}>Refresh Page</button>
      </div>
    );
  }

  const unreadMessagesCount = adminMessages.filter(msg => !msg.is_read).length;
  const readMessages = adminMessages.filter(msg => msg.is_read);
  const unreadMessages = adminMessages.filter(msg => !msg.is_read);

  return (
    <div className={`student-profile ${theme}`} 
         style={{ 
           '--primary': currentSeasonTheme.primary, 
           '--secondary': currentSeasonTheme.secondary, 
           '--accent': currentSeasonTheme.accent,
           '--background': currentSeasonTheme.background 
         }}>
      
      {/* Mobile Menu Toggle */}
      <button 
        className="mobile-menu-toggle"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        <i className="fas fa-bars"></i>
      </button>

      {/* Sidebar Navigation */}
        <aside className={`student-sidebar ${sidebarOpen ? 'mobile-open' : ''}`}>
        {/*<div className="sidebar-logo">
          <div className="logo-icon">AWS</div>
          <span>Amalika World School</span>
        </div>*/}

        <div className="sidebar-logo">
          <img 
            src={logo} 
            alt="Amalika World School" 
            className="logo-image"
          />
          <span>Amalika World School</span>
        </div>

        <nav className="sidebar-nav">
          <button 
            className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            <i className="fas fa-home nav-icon"></i>
            <span className="nav-label">Dashboard</span>
          </button>
          
          <button 
            className={`nav-item ${activeTab === 'courses' ? 'active' : ''}`}
            onClick={() => setActiveTab('courses')}
          >
            <i className="fas fa-book nav-icon"></i>
            <span className="nav-label">Courses</span>
          </button>
          
          <button 
            className={`nav-item ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => setActiveTab('messages')}
          >
            <i className="fas fa-envelope nav-icon"></i>
            <span className="nav-label">Messages</span>
            {unreadMessagesCount > 0 && (
              <span className="notification-badge">
                {unreadMessagesCount}
              </span>
            )}
          </button>
        </nav>

        {/* Settings Dropdown */}
        <div className="settings-container">
          <button 
            className="settings-toggle"
            onClick={() => setSettingsOpen(!settingsOpen)}
          >
            <i className="fas fa-cog"></i>
          </button>
          
          <div className={`settings-dropdown ${settingsOpen ? 'show' : ''}`}>
            <div className="settings-group">
              <h4>Theme</h4>
              <div className="theme-options">
                <button 
                  className={`theme-btn ${theme === 'light' ? 'active' : ''}`}
                  onClick={() => setTheme('light')}
                >
                  <i className="fas fa-sun"></i>
                  Light
                </button>
                <button 
                  className={`theme-btn ${theme === 'dark' ? 'active' : ''}`}
                  onClick={() => setTheme('dark')}
                >
                  <i className="fas fa-moon"></i>
                  Dark
                </button>
              </div>
            </div>
            
            <div className="settings-group">
              <h4>Season</h4>
              <select 
                value={seasonTheme} 
                onChange={(e) => setSeasonTheme(e.target.value)}
                className="season-select"
              >
                <option value="winter">❄️ Winter</option>
                <option value="spring">🌸 Spring</option>
                <option value="summer">☀️ Summer</option>
                <option value="autumn">🍂 Autumn</option>
              </select>
            </div>

            <div className="settings-group">
              <button className="logout-btn" onClick={handleLogout}>
                <i className="fas fa-sign-out-alt"></i>
                Logout
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Header */}
      <header className="student-header">
        <div className="header-content">
          <div className="welcome-section">
            <div className="student-avatar">
              <img 
                src={getStudentImage()} 
                alt={`${studentData?.firstName} ${studentData?.lastName}`}
                onError={handleAvatarError}
              />
            </div>
            <div className="welcome-text">
              <h1>Welcome back, {studentData?.firstName}! 👋</h1>
              <p>Ready to continue your learning journey?</p>
            </div>
          </div>
          
          <div className="header-stats">
            <div className="stat-item">
              <span className="stat-value">{currentCourses.length}</span>
              <span className="stat-label">Active Courses</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{completedCourses.length}</span>
              <span className="stat-label">Completed</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{unreadMessagesCount}</span>
              <span className="stat-label">Unread Messages</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="student-main">
        <div className="tab-content">
          {activeTab === 'dashboard' && (
            <div className="dashboard-tab">
              <div className="dashboard-grid">
                {/* Current Courses */}
                <div className="dashboard-card">
                  <h3>Current Courses ({currentCourses.length})</h3>
                  <div className="courses-list">
                    {currentCourses.length > 0 ? (
                      currentCourses.map(course => (
                        <div key={course.id} className="course-item">
                          <div className="course-info">
                            <h4>{course.name}</h4>
                            <p>Teacher: {course.teacher}</p>
                            <small>Started: {new Date(course.start_date).toLocaleDateString()}</small>
                          </div>
                          <div className="course-progress">
                            <div className="progress-bar">
                              <div 
                                className="progress-fill"
                                style={{ width: `${course.progress}%` }}
                              ></div>
                            </div>
                            <span>{course.progress}% Complete</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      renderEmptyState('courses')
                    )}
                  </div>
                </div>

                {/* Upcoming Classes */}
                <div className="dashboard-card">
                  <h3>Upcoming Classes ({upcomingClasses.length})</h3>
                  <div className="classes-list">
                    {upcomingClasses.length > 0 ? (
                      upcomingClasses.map(classItem => (
                        <div key={classItem.id} className="class-item">
                          <div className="class-time">
                            <strong>{new Date(classItem.date).toLocaleDateString()}</strong>
                            <span>{classItem.time}</span>
                          </div>
                          <div className="class-info">
                            <h4>{classItem.course}</h4>
                            <p>Teacher: {classItem.teacher}</p>
                            <button className="join-btn">Join Class</button>
                          </div>
                        </div>
                      ))
                    ) : (
                      renderEmptyState('classes')
                    )}
                  </div>
                </div>

                {/* Recent Messages Preview */}
                <div className="dashboard-card">
                  <h3>Recent Messages ({adminMessages.length})</h3>
                  <div className="messages-preview">
                    {adminMessages.length > 0 ? (
                      adminMessages.slice(0, 3).map(message => (
                        <div key={message.id} className={`message-preview ${!message.is_read ? 'unread' : ''}`}>
                          <h4>{message.subject}</h4>
                          <p>{message.message.substring(0, 60)}...</p>
                          <span className="message-date">
                            {new Date(message.created_at).toLocaleDateString()}
                            {!message.is_read && <span className="unread-indicator"> • New</span>}
                          </span>
                        </div>
                      ))
                    ) : (
                      renderEmptyState('messages')
                    )}
                  </div>
                  <button 
                    className="view-all-btn"
                    onClick={() => setActiveTab('messages')}
                  >
                    View All Messages
                  </button>
                </div>

                {/* Achievements Preview */}
                <div className="dashboard-card">
                  <h3>Recent Achievements ({achievements.length})</h3>
                  <div className="achievements-preview">
                    {achievements.length > 0 ? (
                      achievements.slice(0, 3).map(achievement => (
                        <div key={achievement.id} className="achievement-item">
                          <div className="achievement-icon">
                            <i className={achievement.icon}></i>
                          </div>
                          <div className="achievement-info">
                            <h4>{achievement.name}</h4>
                            <p>{achievement.description}</p>
                            <span className="achievement-date">
                              {new Date(achievement.date).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      renderEmptyState('achievements')
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'courses' && (
            <div className="courses-tab">
              <div className="courses-section">
                <h3>Current Courses ({currentCourses.length})</h3>
                <div className="courses-grid">
                  {currentCourses.length > 0 ? (
                    currentCourses.map(course => (
                      <div key={course.id} className="course-card">
                        <div className="course-header">
                          <h4>{course.name}</h4>
                          <span className="teacher-name">{course.teacher}</span>
                          <span className="course-level">Level: {course.level}</span>
                          <span className={`status-badge status-${course.status}`}>
                            {course.status.replace('_', ' ')}
                          </span>
                        </div>
                        <div className="progress-section">
                          <div className="progress-bar">
                            <div 
                              className="progress-fill"
                              style={{ width: `${course.progress}%` }}
                            ></div>
                          </div>
                          <span className="progress-text">{course.progress}% Complete</span>
                        </div>
                        <div className="course-dates">
                          <small>Started: {new Date(course.start_date).toLocaleDateString()}</small>
                          {course.end_date && <small>Ends: {new Date(course.end_date).toLocaleDateString()}</small>}
                        </div>
                        <div className="course-actions">
                          <button className="btn-primary">
                            <i className="fas fa-play"></i>
                            Continue Learning
                          </button>
                          <button className="btn-secondary">
                            <i className="fas fa-info-circle"></i>
                            Course Details
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    renderEmptyState('courses')
                  )}
                </div>
              </div>

              <div className="courses-section">
                <h3>Completed Courses ({completedCourses.length})</h3>
                <div className="completed-courses-list">
                  {completedCourses.length > 0 ? (
                    completedCourses.map(course => (
                      <div key={course.id} className="completed-course-card">
                        <div className="course-info">
                          <h4>{course.name}</h4>
                          <span className="completion-date">
                            Completed: {new Date(course.completedDate).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="course-grade">
                          <span className="grade-badge">{course.grade}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    renderEmptyState('completed')
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'messages' && (
            <div className="messages-tab">
              <div className="messages-section">
                <div className="messages-header">
                  <h3>Messages from Admin ({adminMessages.length})</h3>
                  <button 
                    className="btn-primary"
                    onClick={() => setShowMessageModal(true)}
                  >
                    <i className="fas fa-paper-plane"></i>
                    Message Admin
                  </button>
                </div>
                
                {/* Unread Messages */}
                {unreadMessages.length > 0 && (
                  <div className="messages-category">
                    <h4>New Messages ({unreadMessages.length})</h4>
                    <div className="messages-list">
                      {unreadMessages.map(message => (
                        <div key={message.id} className="message-card unread">
                          <div className="message-header">
                            <h4>{message.subject}</h4>
                            <div className="message-actions">
                              <button 
                                className="mark-read-btn"
                                onClick={() => handleMarkAsRead(message.id)}
                              >
                                <i className="fas fa-check"></i>
                                Mark as Read
                              </button>
                              <span className="message-date">
                                {new Date(message.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <p className="message-content">{message.message}</p>
                          <div className="message-status">
                            <span className="unread-status">
                              <i className="fas fa-envelope"></i>
                              New Message
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Read Messages */}
                {readMessages.length > 0 && (
                  <div className="messages-category">
                    <h4>Read Messages ({readMessages.length})</h4>
                    <div className="messages-list">
                      {readMessages.map(message => (
                        <div key={message.id} className="message-card">
                          <div className="message-header">
                            <h4>{message.subject}</h4>
                            <div className="message-actions">
                              <span className="message-date">
                                {new Date(message.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <p className="message-content">{message.message}</p>
                          <div className="message-status">
                            <span className="read-status">
                              <i className="fas fa-check-circle"></i>
                              Read {message.read_at && new Date(message.read_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {adminMessages.length === 0 && renderEmptyState('messages')}
              </div>

              {/* Sent Messages Section */}
              <div className="messages-section">
                <h4>Your Sent Messages ({studentMessages.length})</h4>
                <div className="sent-messages-list">
                  {studentMessages.length > 0 ? (
                    studentMessages.map(message => (
                      <div key={message.id} className="sent-message-card">
                        <div className="sent-message-header">
                          <h5>{message.subject}</h5>
                          <span className="sent-date">
                            Sent: {new Date(message.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="sent-message-content">{message.message}</p>
                      </div>
                    ))
                  ) : (
                    renderEmptyState('sentMessages')
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Send Message Modal */}
      {showMessageModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Send Message to Admin</h3>
              <button 
                className="close-btn"
                onClick={() => setShowMessageModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleSendMessage} className="message-form">
              <div className="form-group">
                <label htmlFor="subject">Subject</label>
                <input
                  type="text"
                  id="subject"
                  value={newMessage.subject}
                  onChange={(e) => setNewMessage({...newMessage, subject: e.target.value})}
                  required
                  placeholder="Enter message subject"
                />
              </div>
              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  value={newMessage.message}
                  onChange={(e) => setNewMessage({...newMessage, message: e.target.value})}
                  required
                  placeholder="Type your message here..."
                  rows="6"
                ></textarea>
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowMessageModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  <i className="fas fa-paper-plane"></i>
                  Send Message
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Live Data Info - Shows real database counts */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'fixed',
          bottom: '10px',
          right: '10px',
          background: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '10px',
          borderRadius: '5px',
          fontSize: '12px',
          zIndex: 10000
        }}>
          <div><strong>Live Database Data:</strong></div>
          <div>Active Courses: {currentCourses.length}</div>
          <div>Completed Courses: {completedCourses.length}</div>
          <div>Admin Messages: {adminMessages.length}</div>
          <div>Sent Messages: {studentMessages.length}</div>
          <div>Unread Messages: {unreadMessagesCount}</div>
          <button 
            onClick={fetchAllStudentData}
            style={{
              background: '#10b981',
              color: 'white',
              border: 'none',
              padding: '5px',
              borderRadius: '3px',
              marginTop: '5px',
              cursor: 'pointer'
            }}
          >
            Refresh Data
          </button>
        </div>
      )}
    </div>
  );
};

export default StudentProfile;